/**
 * AsciiCanvas — character grid renderer (HTML5 Canvas2D).
 *
 * Holds a 2D array of characters (the "live" state) and a base 2D array
 * (the "source" — the unmodified ASCII art loaded from a .txt file).
 * Renders to a single <canvas> element using diff-based redraw — only
 * cells that changed since the last frame are repainted.
 *
 * Why Canvas2D + diff (vs the previous <pre>+innerHTML approach):
 *   The DOM path required rebuilding the entire span tree on every
 *   frame (~4100 spans at 450×135 grid). String concat + parse + DOM
 *   layout was O(total cells) per frame, capping the practical grid
 *   resolution. Canvas2D is O(changed cells) per frame — the painting
 *   has thousands of cells but only ~100-300 mutate per frame from
 *   ambient_flicker / loop_sequence events. The first frame draws
 *   everything; subsequent frames are nearly free.
 *
 * Coordinate system: (x, y) where x is column 0..cols-1, y is row
 * 0..rows-1. Origin top-left.
 */

import { randomNoiseChar, FRAME_CHARS, colorForChar } from "./density.js";

// Default cell size in CSS pixels. Each character occupies cellWidth ×
// cellHeight space on screen. The character itself is drawn at
// `fontSize` (matched to cellHeight) in a monospace font, so the cells
// pack tightly. Tunable via setCellSize().
const DEFAULT_CELL_HEIGHT = 4.5;     // px, matches CSS font-size cap
const DEFAULT_CELL_WIDTH = 2.7;      // px, ≈ 0.6 × fontSize for IBM Plex Mono
const DEFAULT_FONT_FAMILY =
  '"IBM Plex Mono", "Courier Prime", "Courier New", monospace';
const DEFAULT_PAGE_BG = "#f1ebde";   // matches --color-page in CSS

export class AsciiCanvas {
  /**
   * @param {HTMLCanvasElement} canvasEl  the <canvas> to render into
   * @param {number} cols                  grid width in characters
   * @param {number} rows                  grid height in characters
   * @param {object} [opts]
   * @param {number} [opts.cellWidth]      px width per cell (default 3.6)
   * @param {number} [opts.cellHeight]     px height per cell (default 6)
   * @param {string} [opts.fontFamily]     CSS font-family for the canvas
   * @param {string} [opts.pageBg]         page background fill color
   */
  constructor(canvasEl, cols, rows, opts = {}) {
    this.el = canvasEl;
    this.cols = cols;
    this.rows = rows;

    this.cellWidth = opts.cellWidth ?? DEFAULT_CELL_WIDTH;
    this.cellHeight = opts.cellHeight ?? DEFAULT_CELL_HEIGHT;
    this.fontFamily = opts.fontFamily ?? DEFAULT_FONT_FAMILY;
    this.pageBg = opts.pageBg ?? DEFAULT_PAGE_BG;

    this.source = makeBlankGrid(cols, rows, " ");
    this.live = makeBlankGrid(cols, rows, " ");
    this.protectedMask = makeBlankGrid(cols, rows, false);
    this.paletteMap = makeBlankGrid(cols, rows, "sepia");

    // Previous frame's char + color per cell. The render loop diffs
    // against these to decide which cells to redraw. Initially "" /
    // null so the first render dirties everything.
    this.prevChar = makeBlankGrid(cols, rows, "");
    this.prevColor = makeBlankGrid(cols, rows, null);

    this.ctx = canvasEl.getContext("2d", { alpha: false });
    this._configureCanvas();

    // Font readiness: fillText silently falls back to a system font
    // until the web font is loaded. Trigger a re-render once IBM Plex
    // Mono is available so the painting doesn't briefly render in
    // Courier or Menlo.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this._dirtyAll();
        this.render();
      });
    }
  }

  /** Resize the canvas to fit cols × rows at the current cell size. */
  _configureCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const cssW = this.cols * this.cellWidth;
    const cssH = this.rows * this.cellHeight;
    this.el.width = Math.round(cssW * dpr);
    this.el.height = Math.round(cssH * dpr);
    this.el.style.width = cssW + "px";
    this.el.style.height = cssH + "px";

    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = `${this.cellHeight}px ${this.fontFamily}`;
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    // Initial fill with the page background — clearRect alone leaves
    // transparent cells, which would let whatever's behind the canvas
    // bleed through. We want opaque parchment.
    ctx.fillStyle = this.pageBg;
    ctx.fillRect(0, 0, cssW, cssH);

    this._dirtyAll();
  }

  /** Mark every cell as needing redraw on the next render. */
  _dirtyAll() {
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.prevChar[y][x] = "";
        this.prevColor[y][x] = null;
      }
    }
  }

  /** Adjust cell size at runtime (e.g. on viewport resize). */
  setCellSize(cellWidth, cellHeight) {
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    this._configureCanvas();
  }

  /**
   * Load an ASCII art string into the source grid. Pads / truncates to
   * fit the configured cols × rows.
   */
  loadSource(asciiText) {
    const lines = asciiText.replace(/\r/g, "").split("\n");
    for (let y = 0; y < this.rows; y++) {
      const line = lines[y] || "";
      for (let x = 0; x < this.cols; x++) {
        const ch = line[x] ?? " ";
        this.source[y][x] = ch;
        this.protectedMask[y][x] = FRAME_CHARS.has(ch);
      }
    }

    // Bounding box of non-space cells = the framed painting
    let minX = this.cols, minY = this.rows, maxX = -1, maxY = -1;
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.source[y][x] !== " ") {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX >= 0) {
      this.paintingBounds = {
        x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1,
      };
      for (let y = 0; y < this.rows; y++) {
        for (let x = 0; x < this.cols; x++) {
          if (x < minX || x > maxX || y < minY || y > maxY) {
            this.protectedMask[y][x] = true;
          }
        }
      }
    } else {
      this.paintingBounds = { x: 0, y: 0, w: this.cols, h: this.rows };
    }
  }

  /** Replace the live grid with the source (resets any animation). */
  resetToSource() {
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.live[y][x] = this.source[y][x];
      }
    }
  }

  /**
   * Replace the live grid with random noise. Protected cells (frame
   * box-drawing characters) keep their source value, so the frame
   * stays intact while the painting reveals from noise.
   */
  fillWithNoise() {
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        if (this.protectedMask[y][x]) {
          this.live[y][x] = this.source[y][x];
        } else {
          this.live[y][x] = randomNoiseChar();
        }
      }
    }
  }

  /** Set a single cell. Frame cells are immutable. */
  set(x, y, ch) {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return;
    if (this.protectedMask[y][x]) return;
    this.live[y][x] = ch;
  }

  /** Get a single cell (from live grid). */
  get(x, y) {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return " ";
    return this.live[y][x];
  }

  /**
   * Get a single cell from the source grid.
   *
   * Protected cells (frame + outer margin) return " " — animation
   * effects that read source values for drift / restore behavior won't
   * accidentally pull frame characters into the painting interior.
   */
  getSource(x, y) {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return " ";
    if (this.protectedMask[y][x]) return " ";
    return this.source[y][x];
  }

  /**
   * Set per-cell palette overrides (accent regions). See doc on
   * the previous implementation in git history for full notes.
   *
   * @param {Array<{ bounds: { x, y, w, h }, palette: string }>} accents
   */
  setAccents(accents = []) {
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.paletteMap[y][x] = "sepia";
      }
    }
    for (const { bounds, palette } of accents) {
      if (!bounds || !palette) continue;
      const { x: bx, y: by, w, h } = bounds;
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          const x = bx + dx;
          const y = by + dy;
          if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) continue;
          this.paletteMap[y][x] = palette;
        }
      }
    }
    // Palette change implies color change for every cell, so dirty all.
    this._dirtyAll();
  }

  /**
   * Render the live grid to the canvas. Diff-based: only repaints
   * cells whose char-or-color differs from the previous frame.
   *
   * For each dirty cell:
   *   1. fillRect with page background to erase the previous glyph.
   *   2. fillText with the new glyph in its computed color (skipped
   *      if the cell content is a literal space).
   *
   * Cells are batched by color to amortize the ctx.fillStyle state
   * change. Within a batch, cells are drawn one fillText at a time.
   */
  render() {
    const ctx = this.ctx;
    const cw = this.cellWidth;
    const ch = this.cellHeight;
    const pageBg = this.pageBg;

    // Group dirty cells by their new color so we can batch fillStyle.
    const byColor = new Map(); // color -> [[x, y, char], ...]
    const eraseCells = [];

    for (let y = 0; y < this.rows; y++) {
      const liveRow = this.live[y];
      const paletteRow = this.paletteMap[y];
      const prevCharRow = this.prevChar[y];
      const prevColorRow = this.prevColor[y];
      for (let x = 0; x < this.cols; x++) {
        const cell = liveRow[x];
        const color = colorForChar(cell, paletteRow[x]);
        if (cell === prevCharRow[x] && color === prevColorRow[x]) continue;
        // Erase cell first
        eraseCells.push([x, y]);
        if (color !== null && cell !== " ") {
          let bucket = byColor.get(color);
          if (!bucket) {
            bucket = [];
            byColor.set(color, bucket);
          }
          bucket.push([x, y, cell]);
        }
        prevCharRow[x] = cell;
        prevColorRow[x] = color;
      }
    }

    // Erase all dirty cells in one fillStyle.
    if (eraseCells.length > 0) {
      ctx.fillStyle = pageBg;
      for (const [x, y] of eraseCells) {
        ctx.fillRect(x * cw, y * ch, cw, ch);
      }
    }

    // Draw each color bucket.
    for (const [color, cells] of byColor) {
      ctx.fillStyle = color;
      for (const [x, y, cell] of cells) {
        ctx.fillText(cell, x * cw, y * ch);
      }
    }
  }

  /** Clear the live grid to all spaces. */
  clear() {
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        this.live[y][x] = " ";
      }
    }
  }
}

function makeBlankGrid(cols, rows, fill) {
  const grid = new Array(rows);
  for (let y = 0; y < rows; y++) {
    grid[y] = new Array(cols).fill(fill);
  }
  return grid;
}
